import { FundStatus, TrancheVisibility } from 'further-types/fund';
import { TrancheFormInput, TrancheFormValues } from './useTrancheForm';
import { TrancheData } from './types';
import { ApiInputs } from 'lib/trpcClient';

const toFormFile = (file: {
  name: string;
  path: string;
  preview?: string;
}) => ({
  fileName: file.name,
  filePath: file.path,
  previewUri: file.preview,
});

const toApiFile = (field: {
  fileName: string;
  filePath: string;
  previewUri?: string;
}) => ({
  name: field.fileName,
  path: field.filePath,
  preview: field.previewUri,
});

export const mapFormValuesToPayload = (
  data: TrancheFormValues,
): Omit<ApiInputs['fund']['update'], 'id'> => ({
  firmId: data.firmId,
  fundName: data.fundName,
  fundStatus: data.fundStatus,
  startDate: data.startDate ?? undefined,
  finishDate: data.finishDate,
  closedDate: data.closedDate ?? undefined,
  tag: data.tag,
  isProfessionalClientsOnly: data.isProfessionalClientsOnly,
  trancheVisibility: data.trancheVisibility,
  fundRaiseLimit: data.fundRaiseLimit ?? undefined,
  incomeTaxRebate: data.incomeTaxRebate || undefined,
  targetReturn: data.targetReturn || undefined,
  targetPortfolioSize: data.targetPortfolioSize || undefined,
  minimumDirectInvestment: data.minimumDirectInvestment || undefined,
  minimumAdvisedInvestment: data.minimumAdvisedInvestment || undefined,
  minimumIncludesAdviceFee: data.minimumIncludesAdviceFee,
  applicationFormFile: data.applicationFormFile
    ? toApiFile(data.applicationFormFile)
    : null,
  prospectusFile: data.prospectusFile ? toApiFile(data.prospectusFile) : null,
  keyInfoFile: data.keyInfoFile ? toApiFile(data.keyInfoFile) : null,
  additionalDocuments: data.additionalDocuments.map(toApiFile),
  defaultCoreFee: data.defaultCoreFee,
  defaultPerformanceFee: data.defaultPerformanceFee,
  firmBankDetailId: data.firmBankDetailId || undefined,
  title: data.title || undefined,
  customBankTransferReference: data.customBankTransferReference || undefined,
  whatToExpectNow: data.whatToExpectNow || undefined,
  additionalAttachment: data.additionalAttachment
    ? toApiFile(data.additionalAttachment)
    : null,
  additionalInfo: data.additionalInfo || undefined,
});

export const mapTrancheToFormValues = (
  tranche: TrancheData,
): TrancheFormInput => ({
  firmId: tranche.firmId,
  fundName: tranche.fundName,
  fundStatus: tranche.fundStatus ?? FundStatus.Draft,
  startDate: tranche.startDate ?? null,
  finishDate: tranche.finishDate ?? null,
  closedDate: tranche.closedDate ?? null,
  tag: tranche.tag ?? '',
  isProfessionalClientsOnly: tranche.isProfessionalClientsOnly ?? false,
  trancheVisibility: tranche.trancheVisibility ?? TrancheVisibility.ShowOnFirm,
  fundRaiseLimit: tranche.fundRaiseLimit ?? null,
  incomeTaxRebate: tranche.incomeTaxRebate ?? '',
  targetReturn: tranche.targetReturn ?? '',
  targetPortfolioSize: tranche.targetPortfolioSize ?? '',
  minimumDirectInvestment: tranche.minimumDirectInvestment ?? '',
  minimumAdvisedInvestment: tranche.minimumAdvisedInvestment ?? '',
  minimumIncludesAdviceFee: tranche.minimumIncludesAdviceFee ?? false,
  applicationFormFile: tranche.applicationFormFile
    ? toFormFile(tranche.applicationFormFile)
    : null,
  prospectusFile: tranche.prospectusFile
    ? toFormFile(tranche.prospectusFile)
    : null,
  keyInfoFile: tranche.keyInfoFile ? toFormFile(tranche.keyInfoFile) : null,
  additionalDocuments: (tranche.additionalDocuments ?? []).map(toFormFile),
  defaultCoreFee: tranche.coreFeeTemplateId ?? '',
  defaultPerformanceFee: tranche.performanceFeeTemplateId ?? '',
  firmBankDetailId: tranche.firmBankDetailId ?? '',
  title: tranche.title ?? '',
  customBankTransferReference: tranche.customBankTransferReference ?? '',
  whatToExpectNow: tranche.whatToExpectNow ?? '',
  additionalAttachment: tranche.additionalAttachment
    ? toFormFile(tranche.additionalAttachment)
    : null,
  additionalInfo: tranche.additionalInfo ?? '',
});
